import { useEffect, useState } from "react";
import classNames from "classnames";

import * as reportUtils from "./reportDownloadUtils";
import { slugify } from "lib/utils";
import { buildCloudflareImageUrl } from "utils";

import styles from "./card.module.css";
import type { Report } from "content-ui/intelligence/types";
import { useUserData } from "lib/store/user";
import { CheckboxWithText } from "./checkboxWithText/checkboxWithText";

const generateCardImages = (imageUrl) => {
  const baseImage = buildCloudflareImageUrl({
    src: imageUrl,
    width: 280,
    dpr: 1,
    format: "auto",
    quality: 75,
  });

  const image2x = buildCloudflareImageUrl({
    src: baseImage,
    dpr: 2,
  });

  return [baseImage, image2x];
};

const generateSponsorImages = (imageUrl) => {
  const baseSponsorImage = buildCloudflareImageUrl({
    src: imageUrl,
    height: 60,
    dpr: 1,
    fit: "scale-down",
    format: "auto",
    quality: 75,
  });

  const sponsorImage2x = buildCloudflareImageUrl({
    src: baseSponsorImage,
    dpr: 2,
  });

  return [baseSponsorImage, sponsorImage2x];
};

const Sponsor = ({
  image,
  membershipLevel,
  textOverride,
}: {
  image: string;
  membershipLevel: string;
  textOverride?: string;
}) => {
  const [baseSponsorImage, sponsorImage2x] = generateSponsorImages(image);
  return (
    <div
      className="mt-sm block transition-colors duration-300 group-hover:text-cerise"
      style={{ height: 30 }}
    >
      <p className="text-2xs md:mr-sm">
        {textOverride
          ? textOverride
          : membershipLevel === "Pro"
            ? "Read exclusively with"
            : "In partnership with"}
      </p>
      <img
        src={baseSponsorImage}
        srcSet={`${baseSponsorImage} 1x, ${sponsorImage2x} 2x`}
        // TODO: need sponsor name for alt tag
        alt="Sponsor logo"
        className={styles.sponsorImage}
        loading="lazy"
      />
    </div>
  );
};

const Card = ({
  report,
  openModal,
}: {
  report: Report;
  openModal: Function;
}) => {
  const { hasMissingInfo } = useUserData();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const cardClasses = classNames("group", styles.card);
  const [checked, setCheck] = useState(false);
  const reportURL = "/intelligence/reports/" + slugify(report.title);
  const { readerSubscriptionLevel } = useUserData();
  const [baseReportImage, reportImage2x] = generateCardImages(report.image);
  const { isAuthorisedUser } = useUserData();

  const renderRegisteredDownload = (report: Report) => {
    const onClickDownload = () => {
      if (hasMissingInfo) {
        completeProfileRedirect();
      } else {
        reportUtils.downloadReportClick(
          report.pdf,
          report.title,
          checked,
          report.slug // Could not get an ID from WP ACF, so using the slug
        );
      }
    };

    const onClickRegister = () => {
      // This is made on purpose - we need to force a page reload to get rendered components from zephr proxy
      window.location.href = `/members/login?report=${encodeURIComponent(report.title)}`;
    };

    const completeProfileRedirect = () => {
      localStorage.setItem("report_redirect", report.title);
      // This is made on purpose - we need to force a page reload to get rendered components from zephr proxy
      window.location.href = "/members/complete-profile?redirectTarget=report";
    };

    const isConsentRequired =
      report.marketingConsentRequired === "Mandatory opt in";

    return (
      <div>
        {report.marketingConsentRequired === "Optional opt in" ||
        report.marketingConsentRequired === "Mandatory opt in" ? (
          <div className="mt-4">
            <CheckboxWithText
              id={`reports_third_party-${report.slug}`}
              testId={"reports-consent-card"}
              onChange={(e) => setCheck(e.target.checked)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: report.sponsorCheckboxText.replaceAll(
                    /<p>|<\/p>/gi,
                    ""
                  ),
                }}
              />
            </CheckboxWithText>
          </div>
        ) : null}

        {isAuthorisedUser ? (
          <span>
            {/* Logged In */}
            {reportUtils.canAccessContent(report, readerSubscriptionLevel!) &&
            hasMounted ? (
              <button
                disabled={isConsentRequired && !checked}
                className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white disabled:bg-gray-500 md:mt-xl md:w-auto md:text-xs"
                onClick={onClickDownload}
              >
                Download report
              </button>
            ) : (
              <button
                className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white md:mt-xl md:w-auto md:text-xs"
                onClick={() => {
                  if (report.membershipLevel === "Pro") {
                    openModal(true);
                  } else {
                    window.location.assign("/subscribe");
                  }
                }}
              >
                {readerSubscriptionLevel !== "pro" &&
                report.membershipLevel === "Pro"
                  ? "Join Pro to download"
                  : "Join to download"}
              </button>
            )}
          </span>
        ) : (
          <span>
            {/* Logged Out */}
            <button
              className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white md:mt-xl md:w-auto md:text-xs"
              onClick={() => {
                report.membershipLevel === "Pro"
                  ? openModal(true)
                  : onClickRegister();
              }}
            >
              {report.membershipLevel === "Pro"
                ? "Join Pro to Download"
                : "Join to Download"}
            </button>
          </span>
        )}
      </div>
    );
  };

  const renderOpenDownload = (report: Report) => {
    const reportLink = report.pdf || report.url;
    return (
      <button
        className="mt-lg inline-block w-full bg-black px-6 py-3 text-center font-bold text-2xs text-white md:mt-xl md:w-auto md:text-xs"
        onClick={() =>
          reportUtils.downloadReportClick(
            reportLink,
            report.title,
            false,
            report.slug // Could not get an ID from WP ACF, so using the slug
          )
        }
      >
        Download report
      </button>
    );
  };

  // This is an urgent request from the client. The logic for amending the text for all
  // reports is being cooked up by product, so this commit can be reverted once that's done.
  const sponsorTextOverride =
    report.title === "Spacetech" ? "Sponsored by" : undefined;

  return (
    <>
      {hasMounted && (
        <div className={cardClasses}>
          <a
            className="block transition-colors duration-300 group-hover:text-cerise"
            href={reportURL}
          >
            <img
              className="shadow-lg md:shadow-xl"
              src={baseReportImage}
              srcSet={`${baseReportImage} 1x, ${reportImage2x} 2x`}
              alt="Report cover"
              loading="lazy"
            />

            <p className="whitespace-pre-wrap pt-md font-bold text-sm leading-tight md:pt-2xl md:text-lg">
              {report.title}
            </p>

            {report.description?.trim() ? (
              <p className="mt-sm whitespace-pre-wrap text-xs md:mt-lg md:text-sm">
                {report.description}
              </p>
            ) : null}
          </a>

          <div>
            {report.membershipLevel !== "Free"
              ? renderRegisteredDownload(report)
              : renderOpenDownload(report)}

            {report.sponsorImage ? (
              <Sponsor
                image={report.sponsorImage}
                membershipLevel={report.membershipLevel}
                textOverride={sponsorTextOverride}
              />
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default Card;
